import * as m from '@staiter/i18n';
import { createFileRoute } from '@tanstack/react-router';
import { TaskCardFullPage } from '~/components/tasks/card/TaskCardFullPage';
import { TaskLoadFailed, TaskNotFound } from '~/components/tasks/card/TaskNotFound';
import { taskAccessOutcome } from '~/lib/tasks/queries/task-not-found';
import { useTaskLoadFailure } from '~/lib/tasks/queries/useTaskLoadFailure';
import { useTask } from '~/lib/tasks/queries/useTasks';
import { useAuthStore } from '~/store/auth';

/**
 * FB6 (M2M) — project-agnostic карточка `/work-tasks/task/$taskId`.
 *
 * Идентичность задачи не привязана к проекту: ссылка живёт при смене главного
 * и при отвязке любых проектов. Контекст рендера = главный проект задачи
 * (статус редактируемый — карточка открыта «в главном»); внутрипроектный
 * маршрут `$projectId/task/$taskId` остаётся контекстным входом.
 */

export const Route = createFileRoute('/_auth/work-tasks/task/$taskId')({
  staticData: { minMainWidth: 792 },
  component: AgnosticTaskCardRoute,
});

function AgnosticTaskCardRoute(): JSX.Element {
  const { taskId } = Route.useParams();
  // F20 — id актора для карточки: без него корзина вложений и claim-логика
  // не знают, «я» это или нет (проп годами дефолтил в пустую строку).
  const currentUserId = useAuthStore((s) => s.user?.user_id ?? s.user?.id ?? '');
  // Detail-bundle без project-контекста (BB6): primary — из задачи.
  const taskQuery = useTask(taskId);
  const primaryProjectId = taskQuery.data?.task.project_id;

  // F7 — обёртка маршрута тоже обязана иметь ветку ошибки: раньше любая
  // недоступность задачи оставляла её в вечном `tm_loading()`.
  const accessOutcome = taskAccessOutcome(taskQuery.error);
  const loadFailure = useTaskLoadFailure(taskQuery);
  if (accessOutcome !== null) {
    // Проект здесь не известен: маршрут project-agnostic, а задача не загрузилась —
    // взять `project_id` неоткуда, поэтому выход один, общий (UX-A4).
    return <TaskNotFound variant={accessOutcome} taskId={taskId} />;
  }

  // UX-A2 — 500 и сетевой сбой раньше уходили в бесконечный `tm_loading()`.
  if (loadFailure.failed) {
    return (
      <TaskLoadFailed
        traceId={loadFailure.traceId}
        onRetry={() => void taskQuery.refetch()}
        retrying={loadFailure.retrying}
      />
    );
  }

  if (!primaryProjectId) {
    return <div style={{ padding: 'var(--space-4)' }}>{m.tm_loading()}</div>;
  }
  return (
    <TaskCardFullPage taskId={taskId} projectId={primaryProjectId} currentUserId={currentUserId} />
  );
}
